import React, { useEffect, useState, useCallback, useRef } from 'react';
import { User, Mail, Phone, KeyRound, Loader2 } from 'lucide-react';
import OtpBoxInput from './OtpBoxInput';
import PhoneInput from './PhoneInput';
import { requestOtpWithCaptcha, verifyOtp, portalErrorMessage } from '../api/portal';
import './RegisterGate.css';

const RESEND_SECONDS = 45;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function loadTurnstileScript() {
  if (!TURNSTILE_SITE_KEY || document.getElementById('cf-turnstile-script')) return;
  const script = document.createElement('script');
  script.id = 'cf-turnstile-script';
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/** Full name + email + phone -> Turnstile-protected OTP send -> OTP verify -> onVerified(account).
 * showName/showPhone=false (owner correction, 2026-07-25 — LoginPage's "sign in with a code"
 * recovery path): that screen is for an EXISTING account proving it owns the email, not a fresh
 * capture — asking for a name/phone that's just discarded is pure friction with no benefit. Hidden
 * entirely (not merely optional) in that mode; registration (ApplyPage, the defaults) is unchanged. */
export default function RegisterGate({ onVerified, title = 'Create your account', requireName = true, showName = true, showPhone = true }) {
  const [step, setStep] = useState('contact'); // 'contact' | 'code'
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Render the Cloudflare Turnstile widget once and keep it mounted, so both the initial "Continue"
  // and a later "Resend code" have a fresh token. No site key (local/dev) -> no widget, empty token,
  // which captcha.ts's server-side dev fallback accepts.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    loadTurnstileScript();
    let cancelled = false;
    const render = () => {
      if (cancelled) return;
      if (!window.turnstile || !turnstileRef.current) { setTimeout(render, 300); return; }
      if (widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        'error-callback': () => setCaptchaToken(''),
        'expired-callback': () => setCaptchaToken(''),
      });
    };
    render();
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* widget already gone */ }
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const captchaReady = !TURNSTILE_SITE_KEY || !!captchaToken;
  const canContinue = (!showName || !requireName || fullName.trim()) && emailValid && (!showPhone || phone.trim()) && captchaReady;
  // Server-side account fields stay firstName/lastName (deriveNameSplit already does this exact
  // split for legacy name-only accounts) — a single visible field here, matching the artifact,
  // doesn't require touching that contract downstream (ApplyPage, PUT /me, CV/document naming).
  const splitName = () => {
    const trimmed = fullName.trim().replace(/\s+/g, ' ');
    const spaceAt = trimmed.indexOf(' ');
    return spaceAt === -1
      ? { firstName: trimmed, lastName: '' }
      : { firstName: trimmed.slice(0, spaceAt), lastName: trimmed.slice(spaceAt + 1) };
  };

  const sendCode = useCallback(async () => {
    setBusy(true); setError('');
    try {
      await requestOtpWithCaptcha(email.trim(), captchaToken);
      setStep('code');
      setResendIn(RESEND_SECONDS);
    } catch (err) { setError(portalErrorMessage(err, 'Could not send the code. Please try again.')); }
    finally {
      // Turnstile tokens are single-use — refresh the widget after every attempt (incl. resend).
      setCaptchaToken('');
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* widget not ready */ }
      }
      setBusy(false);
    }
  }, [email, captchaToken]);

  async function handleContactSubmit(e) {
    e?.preventDefault();
    if (!canContinue || busy) return;
    await sendCode();
  }

  async function handleVerify(e) {
    e?.preventDefault();
    if (code.trim().length !== 6 || busy) return;
    setBusy(true); setError('');
    try {
      const acc = await verifyOtp(email.trim(), code.trim());
      // hadExistingName reflects the ACCOUNT's own pre-existing state (before the spread below
      // overwrites firstName/lastName with whatever was just typed) — callers that only want to
      // persist a brand-new account's freshly-typed name (e.g. LoginPage's sign-in reuse) need this
      // to survive the overwrite; ApplyPage's registration path ignores it (always persists there).
      const hadExistingName = !!acc.firstName;
      const { firstName, lastName } = splitName();
      onVerified({ ...acc, firstName, lastName, phone: phone.trim(), hadExistingName });
    } catch (err) { setError(portalErrorMessage(err, 'That code did not work. Please try again.')); }
    finally { setBusy(false); }
  }

  return (
    <div className="registergate">
      <h2 className="authgate-title">{step === 'contact' ? title : 'Verify your email'}</h2>
      {error && <div className="apply-error" role="alert">{error}</div>}

      {/* Inputs only — the submit button is moved BELOW the Turnstile (owner ask 2026-07-27: captcha above
          the Continue button). The button lives outside the form and is wired back via `form="…"`. */}
      {step === 'contact' && (
        <form id="rg-form-contact" className="apply-step" onSubmit={handleContactSubmit}>
          {showName && (
            <>
              <label className="apply-label" htmlFor="rg-name">
                <User size={18} /> Full name {!requireName && <span className="apply-optional">(optional)</span>}
              </label>
              <input id="rg-name" type="text" autoComplete="name" autoFocus
                className="apply-input" placeholder="As on your passport"
                value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </>
          )}
          <label className="apply-label" htmlFor="rg-email" style={showName ? { marginTop: '1rem' } : undefined}>
            <Mail size={18} /> Email
          </label>
          <input id="rg-email" type="email" inputMode="email" autoComplete="email" autoFocus={!showName}
            className="apply-input" placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} />
          {showPhone && (
            <>
              <label className="apply-label" htmlFor="rg-phone" style={{ marginTop: '1rem' }}>
                <Phone size={18} /> Phone number
              </label>
              <PhoneInput id="rg-phone" value={phone} onChange={setPhone} />
            </>
          )}
        </form>
      )}

      {step === 'code' && (
        <form id="rg-form-code" className="apply-step" onSubmit={handleVerify}>
          <label className="apply-label" htmlFor="rg-code"><KeyRound size={18} /> Enter the 6-digit code</label>
          <p className="apply-hint">Sent to <strong>{email}</strong>. It expires in 10 minutes.</p>
          <OtpBoxInput value={code} onChange={setCode} disabled={busy} />
        </form>
      )}

      {/* Turnstile ABOVE the submit button (owner ask 2026-07-27). Rendered ONCE + kept mounted (single
          widget, outside the step forms) so its token survives the contact→code step change for resend. */}
      {TURNSTILE_SITE_KEY && (
        <div className="registergate-captcha" style={{ marginTop: '1rem' }}>
          <div ref={turnstileRef} className="registergate-turnstile" />
          <p className="registergate-captcha-note">Protected by Cloudflare Turnstile.</p>
        </div>
      )}

      {/* Submit buttons — outside the form (associated via `form=`) so they sit BELOW the Turnstile. */}
      {step === 'contact' && (
        <button type="submit" form="rg-form-contact" className="apply-btn" disabled={!canContinue || busy}>
          {busy ? <Loader2 className="spin" size={18} /> : null} Continue
        </button>
      )}

      {step === 'code' && (
        <>
          <button type="submit" form="rg-form-code" className="apply-btn" disabled={code.trim().length !== 6 || busy}>
            {busy ? <Loader2 className="spin" size={18} /> : null} Verify &amp; continue
          </button>
          <button type="button" className="apply-link-btn" disabled={resendIn > 0 || busy || !captchaReady} onClick={sendCode}>
            {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
          </button>
          <button type="button" className="apply-link-btn" onClick={() => { setCode(''); setError(''); setStep('contact'); }}>
            Edit your details
          </button>
        </>
      )}
    </div>
  );
}
